<template>
  <div>
    <b-card
      v-if="itemData && itemData.id"
    >
      <div class="d-flex justify-content-between align-items-center border-bottom pb-1 mb-1">
        <h2 class="text-primary mb-0">
          {{ itemData.title }}
        </h2>
        <div>
          <b-button
            v-if="!isLocked && canWrite"
            variant="primary"
            pill
            :to="'/app/projects/edit/' + itemData.id"
          >
            <feather-icon icon="EditIcon" />
            {{ $t('common.edit') }}
          </b-button>
        </div>
      </div>
      <b-row>
        <b-col
          cols="12"
          md="10"
        >
          <b-table-simple
            bordered
          >
            <b-tbody>
              <b-tr>
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('projects.detail.visibility') }}
                </b-th>
                <b-td>
                  {{ isPublic ? $t('projects.detail.public') : $t('projects.detail.private') }}
                </b-td>
              </b-tr>
              <b-tr v-if="!isPublic">
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('projects.detail.managers') }}
                </b-th>
                <b-td>
                  {{ managerNames || '—' }}
                </b-td>
              </b-tr>
              <b-tr v-if="!isPublic">
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('projects.detail.watchers') }}
                </b-th>
                <b-td>
                  {{ watcherNames || '—' }}
                </b-td>
              </b-tr>
              <b-tr>
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('fields.targetDate') }}
                </b-th>
                <b-td>
                  {{ itemData.target_date ? moment(itemData.target_date).format('DD.MM.YYYY') : '—' }}
                </b-td>
              </b-tr>
              <b-tr>
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('projects.detail.createdDate') }}
                </b-th>
                <b-td>
                  {{ itemData.created ? moment(itemData.created).format('DD.MM.YYYY') : '—' }}
                </b-td>
              </b-tr>
              <b-tr>
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('fields.department') }}
                </b-th>
                <b-td>
                  {{ itemData.department || '—' }}
                </b-td>
              </b-tr>
              <b-tr>
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('fields.team') }}
                </b-th>
                <b-td>
                  {{ itemData.team || '—' }}
                </b-td>
              </b-tr>
              <b-tr>
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('projects.detail.createdBy') }}
                </b-th>
                <b-td>
                  {{ itemData.username || '—' }}
                </b-td>
              </b-tr>
              <b-tr v-if="itemData.completed_date">
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('projects.detail.completedDate') }}
                </b-th>
                <b-td>
                  {{ itemData.completed_date? moment(itemData.completed_date).format('DD.MM.YYYY') : '-' }}
                </b-td>
              </b-tr>
              <b-tr v-if="itemData.completed_by_name">
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('projects.detail.completedBy') }}
                </b-th>
                <b-td>
                  {{ itemData.completed_by_name? itemData.completed_by_name : '-' }}
                </b-td>
              </b-tr>
              <b-tr v-if="itemData.cancelled_date">
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('projects.detail.cancelledDate') }}
                </b-th>
                <b-td>
                  {{ itemData.cancelled_date? moment(itemData.cancelled_date).format('DD.MM.YYYY') : '-' }}
                </b-td>
              </b-tr>
              <b-tr v-if="itemData.cancelled_by_name">
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('projects.detail.cancelledBy') }}
                </b-th>
                <b-td>
                  {{ itemData.cancelled_by_name }}
                </b-td>
              </b-tr>
              <b-tr v-if="itemData.description">
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('fields.description') }}
                </b-th>
                <b-td>
                  {{ itemData.description? itemData.description : '-' }}
                </b-td>
              </b-tr>
              <b-tr v-if="itemData.closing_note">
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('projects.detail.closingNote') }}
                </b-th>
                <b-td>
                  {{ itemData.closing_note }}
                </b-td>
              </b-tr>
              <b-tr v-if="itemData.cancel_reason">
                <b-th class="width-200 text-nowrap bg-light">
                  {{ $t('projects.detail.cancelReason') }}
                </b-th>
                <b-td>
                  {{ itemData.cancel_reason }}
                </b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
        </b-col>
        <b-col
          cols="12"
          md="2"
        >
          <div class="border p-1 rounded text-center mb-1">
            <div
              class="font-weight-bolder"
              style="margin-bottom: 5px"
            >
              {{ $t('fields.status') }}
            </div>
            <b-badge
              :variant="statusVariant"
              class="w-100"
            >
              {{ statusLabel }}
            </b-badge>
          </div>
          <div class="border p-1 rounded text-center">
            <div
              class="font-weight-bolder"
              style="margin-bottom: 5px"
            >
              {{ $t('projects.detail.tasks') }}
            </div>
            <b-badge
              variant="warning"
              class="w-100"
            >
              {{ $t('projects.detail.taskProgress', { done: itemData.completedTaskCount || 0, total: itemData.taskCount || 0 }) }}
            </b-badge>
          </div>
        </b-col>
      </b-row>
    </b-card>
    <b-alert
      v-else-if="notFound"
      show
      variant="warning"
    >
      <div class="alert-body">
        {{ $t('projects.detail.notFound') }}
      </div>
    </b-alert>
    <loading v-else />
  </div>
</template>

<script>
import {
  BAlert, BCard, BRow, BCol, BButton, BBadge, BTableSimple, BTbody, BTr, BTh, BTd,
} from 'bootstrap-vue'
import Loading from '@/layouts/components/common/Loading.vue'
import { canWriteProject } from '@/auth/taskPermissions'
import { definitionLabel, statusSlug } from '@/libs/definitions'

const STATUS_VARIANT_MAP = {
  open: 'light-info',
  completed: 'light-success',
  cancelled: 'light-danger',
}

export default {
  name: 'ProjectsDetail',
  components: {
    BAlert,
    BCard,
    BRow,
    BCol,
    BButton,
    BBadge,
    Loading,
    BTableSimple,
    BTbody,
    BTr,
    BTh,
    BTd,
  },
  data() {
    return {
      loaded: false,
    }
  },
  computed: {
    notFound() {
      return this.loaded && !this.loading && !this.itemData?.id
    },
    canWrite() {
      return canWriteProject(this.itemData)
    },
    isPublic() {
      return !!Number(this.itemData?.is_public)
    },
    managerNames() {
      const users = this.itemData?.manager_users
      return users && users.length ? users.map(u => u.name).join(', ') : null
    },
    watcherNames() {
      const watchers = this.itemData?.watchers
      if (!watchers || !watchers.length) return null
      return watchers.map(w => (w.watcher_type === 'user' ? w.name : w.team)).filter(Boolean).join(', ')
    },
    itemData() {
      return this.$store.getters['projects/dataItem']
    },
    loading() {
      return this.$store.getters['projects/loading']
    },
    currentStatus() {
      return statusSlug('project', this.itemData)
    },
    isLocked() {
      return ['completed', 'cancelled'].includes(this.currentStatus)
    },
    statusVariant() {
      return STATUS_VARIANT_MAP[this.currentStatus] || 'light-secondary'
    },
    statusLabel() {
      return definitionLabel('project', this.itemData?.project_status_slug, this.itemData?.project_status)
    },
  },
  created() {
    this.$store.dispatch('projects/getDataItem', this.$route.params.id)
      .finally(() => { this.loaded = true })
  },
}
</script>
